import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { Plus, Building2, Edit2, Trash2, Send, Mail, Phone, User, Calendar, DollarSign, HardHat, ChevronDown, ChevronUp, FileBarChart } from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';
import { useAuth } from '@/context/AuthContext';

export default function SubContractorManager({ projectId, project }) {
  const { user } = useAuth();
  const [subcontractors, setSubcontractors] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedForRequest, setSelectedForRequest] = useState(null);

  const [formData, setFormData] = useState({
    company_name: '',
    trading_name: '',
    company_registration: '',
    vat_number: '',
    physical_address: '',
    postal_address: '',
    telephone: '',
    fax: '',
    email: '',
    website: '',
    primary_contact: { name: '', surname: '', position: '', email: '', mobile: '', direct_tel: '' },
    secondary_contact: null,
    trade: '',
    trade_other: '',
    subcontract_number: '',
    contract_value: '',
    contract_start_date: '',
    contract_end_date: '',
    scope_description: '',
    program_submission_required: true,
    program_submission_frequency: 'monthly'
  });

  const [requestForm, setRequestForm] = useState({
    request_type: 'update',
    subject: '',
    message: '',
    due_date: '',
    reminder_days_before: 3
  });

  const normalizedRole = user?.role?.toLowerCase()?.replace(/\s+/g, '_') || '';
  const canManage = ['admin', 'director', 'project_director', 'project_manager',
    'contracts_manager', 'commercial_manager', 'quantity_surveyor', 'procurement_manager',
    'contractor', 'qs', 'cm'].includes(normalizedRole);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [scResponse, tradesResponse] = await Promise.all([
        api.subcontractors.getAll(projectId),
        api.subcontractors.getTrades()
      ]);
      setSubcontractors(scResponse.data || []);
      setTrades(tradesResponse.data || []);
    } catch (error) {
      toast.error('Failed to load sub-contractors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      trading_name: '',
      company_registration: '',
      vat_number: '',
      physical_address: '',
      postal_address: '',
      telephone: '',
      fax: '',
      email: '',
      website: '',
      primary_contact: { name: '', surname: '', position: '', email: '', mobile: '', direct_tel: '' },
      secondary_contact: null,
      trade: '',
      trade_other: '',
      subcontract_number: '',
      contract_value: '',
      contract_start_date: '',
      contract_end_date: '',
      scope_description: '',
      program_submission_required: true,
      program_submission_frequency: 'monthly'
    });
    setEditingId(null);
  };

  const handleEdit = (sc) => {
    setFormData({
      ...sc,
      contract_value: sc.contract_value || '',
      primary_contact: sc.primary_contact || { name: '', surname: '', position: '', email: '', mobile: '', direct_tel: '' }
    });
    setEditingId(sc.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null,
        secondary_contact: formData.secondary_contact?.name ? formData.secondary_contact : null
      };

      if (editingId) {
        await api.subcontractors.update(projectId, editingId, submitData);
        toast.success('Sub-contractor updated');
      } else {
        await api.subcontractors.create(projectId, submitData);
        toast.success('Sub-contractor added');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sub-contractor');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete sub-contractor "${name}"? This cannot be undone.`)) return;
    try {
      await api.subcontractors.delete(projectId, id);
      toast.success('Sub-contractor deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete sub-contractor');
    }
  };

  const handleRequestProgram = async (e) => {
    e.preventDefault();
    try {
      const response = await api.programs.createRequest(projectId, {
        subcontractor_id: selectedForRequest.id,
        ...requestForm
      });
      toast.success('Program request created');
      setRequestDialogOpen(false);
      setSelectedForRequest(null);
      // Show the upload link
      if (response.data.upload_url) {
        toast.info(`Upload link: ${window.location.origin}${response.data.upload_url}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create program request');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700 border-green-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200',
      terminated: 'bg-red-100 text-red-700 border-red-200',
      on_hold: 'bg-amber-100 text-amber-700 border-amber-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `R ${parseFloat(value).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading sub-contractors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-barlow font-semibold uppercase">Sub-Contractors</h2>
          <p className="text-sm text-slate-500 mt-1">
            {subcontractors.length} sub-contractor{subcontractors.length !== 1 ? 's' : ''} registered
          </p>
        </div>
        <div className="flex gap-2">
          <PrintScheduleButton moduleType="Subcontractor" data={subcontractors} project={project} />
          {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-subcontractor-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-3" size="sm">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Sub-Contractor</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-barlow font-bold uppercase">
                  {editingId ? 'Edit Sub-Contractor' : 'Add Sub-Contractor'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company Details */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b pb-2">Company Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Company Name *</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                        className="rounded-sm"
                        data-testid="sc-company-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Trading Name</Label>
                      <Input
                        value={formData.trading_name}
                        onChange={(e) => setFormData({ ...formData, trading_name: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Registration Number</Label>
                      <Input
                        value={formData.company_registration}
                        onChange={(e) => setFormData({ ...formData, company_registration: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">VAT Number</Label>
                      <Input
                        value={formData.vat_number}
                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Trade Selection */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b pb-2">Trade / Discipline</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Trade *</Label>
                      <Select value={formData.trade} onValueChange={(v) => setFormData({ ...formData, trade: v })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select trade" />
                        </SelectTrigger>
                        <SelectContent>
                          {trades.map((trade) => (
                            <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.trade === 'Other' && (
                      <div>
                        <Label className="text-xs font-bold uppercase text-slate-500">Specify Trade</Label>
                        <Input
                          value={formData.trade_other}
                          onChange={(e) => setFormData({ ...formData, trade_other: e.target.value })}
                          className="rounded-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b pb-2">Contact Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-bold uppercase text-slate-500">Physical Address</Label>
                      <Textarea
                        value={formData.physical_address}
                        onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                        rows={2}
                        className="rounded-sm resize-none"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Telephone</Label>
                      <Input
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Primary Contact */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b pb-2">Primary Contact *</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Name *</Label>
                      <Input
                        value={formData.primary_contact.name}
                        onChange={(e) => setFormData({
                          ...formData,
                          primary_contact: { ...formData.primary_contact, name: e.target.value }
                        })}
                        required
                        className="rounded-sm"
                        data-testid="sc-contact-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Surname *</Label>
                      <Input
                        value={formData.primary_contact.surname}
                        onChange={(e) => setFormData({
                          ...formData,
                          primary_contact: { ...formData.primary_contact, surname: e.target.value }
                        })}
                        required
                        className="rounded-sm"
                        data-testid="sc-contact-surname"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Position</Label>
                      <Input
                        value={formData.primary_contact.position}
                        onChange={(e) => setFormData({
                          ...formData,
                          primary_contact: { ...formData.primary_contact, position: e.target.value }
                        })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Email *</Label>
                      <Input
                        type="email"
                        value={formData.primary_contact.email}
                        onChange={(e) => setFormData({
                          ...formData,
                          primary_contact: { ...formData.primary_contact, email: e.target.value }
                        })}
                        required
                        className="rounded-sm"
                        data-testid="sc-contact-email"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Mobile</Label>
                      <Input
                        value={formData.primary_contact.mobile}
                        onChange={(e) => setFormData({
                          ...formData,
                          primary_contact: { ...formData.primary_contact, mobile: e.target.value }
                        })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Direct Tel</Label>
                      <Input
                        value={formData.primary_contact.direct_tel}
                        onChange={(e) => setFormData({
                          ...formData,
                          primary_contact: { ...formData.primary_contact, direct_tel: e.target.value }
                        })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Contract Details */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b pb-2">Contract Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Subcontract Number</Label>
                      <Input
                        value={formData.subcontract_number}
                        onChange={(e) => setFormData({ ...formData, subcontract_number: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Contract Value (R)</Label>
                      <Input
                        type="number"
                        value={formData.contract_value}
                        onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Start Date</Label>
                      <Input
                        type="date"
                        value={formData.contract_start_date}
                        onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                        className="rounded-sm font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">End Date</Label>
                      <Input
                        type="date"
                        value={formData.contract_end_date}
                        onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                        className="rounded-sm font-mono"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-bold uppercase text-slate-500">Scope Description</Label>
                      <Textarea
                        value={formData.scope_description}
                        onChange={(e) => setFormData({ ...formData, scope_description: e.target.value })}
                        rows={3}
                        className="rounded-sm resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Program Submission */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b pb-2">Program Submission Settings</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="program_required"
                        checked={formData.program_submission_required}
                        onChange={(e) => setFormData({ ...formData, program_submission_required: e.target.checked })}
                        className="rounded"
                      />
                      <Label htmlFor="program_required" className="text-sm">Program Submission Required</Label>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Submission Frequency</Label>
                      <Select 
                        value={formData.program_submission_frequency} 
                        onValueChange={(v) => setFormData({ ...formData, program_submission_frequency: v })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="fortnightly">Fortnightly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
                    {editingId ? 'Update' : 'Add'} Sub-Contractor
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-xs font-bold uppercase text-slate-500">Total</p>
          <p className="text-xl sm:text-2xl font-bold">{subcontractors.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-xs font-bold uppercase text-green-600">Active</p>
          <p className="text-xl sm:text-2xl font-bold text-green-600">
            {subcontractors.filter(s => s.status === 'active').length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-xs font-bold uppercase text-amber-600">Program Due</p>
          <p className="text-xl sm:text-2xl font-bold text-amber-600">
            {subcontractors.filter(s => s.program_submission_required && s.status === 'active').length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-xs font-bold uppercase text-slate-500">Total Value</p>
          <p className="text-sm sm:text-lg font-bold truncate">
            {formatCurrency(subcontractors.reduce((sum, s) => sum + (s.contract_value || 0), 0))}
          </p>
        </div>
      </div>

      {/* Sub-contractors List */}
      {subcontractors.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
          <HardHat className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-barlow font-bold uppercase text-slate-500 mb-2">No Sub-Contractors</h3>
          <p className="text-sm text-slate-400 mb-4">Add sub-contractors to manage program submissions and track their work packages.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {subcontractors.map((sc) => (
            <div key={sc.id} className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid={`subcontractor-${sc.id}`}>
              {/* Header Row */}
              <div 
                className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors gap-2"
                onClick={() => setExpandedId(expandedId === sc.id ? null : sc.id)}
              >
                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className="bg-slate-100 rounded-full p-2 shrink-0">
                    <Building2 className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-sm sm:text-base truncate">{sc.company_name}</h4>
                      <Badge className={`${getStatusColor(sc.status)} text-xs`}>{sc.status}</Badge>
                      <Badge variant="outline" className="text-xs">{sc.trade}</Badge>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 truncate">
                      {sc.primary_contact?.name} {sc.primary_contact?.surname} 
                      {sc.primary_contact?.email && ` - ${sc.primary_contact.email}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 ml-10 sm:ml-0">
                  {sc.contract_value && (
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-slate-500 uppercase">Value</p>
                      <p className="font-mono font-bold text-sm sm:text-base">{formatCurrency(sc.contract_value)}</p>
                    </div>
                  )}
                  {expandedId === sc.id ? (
                    <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === sc.id && (
                <div className="border-t border-slate-200 p-3 sm:p-4 bg-slate-50">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    {/* Contact Info */}
                    <div>
                      <h5 className="text-xs font-bold uppercase text-slate-500 mb-2">Contact Information</h5>
                      <div className="space-y-1 text-sm">
                        {sc.telephone && (
                          <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-400" />
                            {sc.telephone}
                          </p>
                        )}
                        {sc.email && (
                          <p className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-400" />
                            {sc.email}
                          </p>
                        )}
                        {sc.physical_address && (
                          <p className="text-slate-600 mt-2">{sc.physical_address}</p>
                        )}
                      </div>
                    </div>

                    {/* Contract Details */}
                    <div>
                      <h5 className="text-xs font-bold uppercase text-slate-500 mb-2">Contract Details</h5>
                      <div className="space-y-1 text-sm">
                        {sc.subcontract_number && (
                          <p><span className="text-slate-500">No:</span> {sc.subcontract_number}</p>
                        )}
                        {sc.contract_start_date && (
                          <p className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            {sc.contract_start_date} - {sc.contract_end_date || 'TBD'}
                          </p>
                        )}
                        {sc.scope_description && (
                          <p className="text-slate-600 mt-2">{sc.scope_description}</p>
                        )}
                      </div>
                    </div>

                    {/* Program Settings */}
                    <div>
                      <h5 className="text-xs font-bold uppercase text-slate-500 mb-2">Program Settings</h5>
                      <div className="space-y-1 text-sm">
                        <p>
                          <span className="text-slate-500">Required:</span>{' '}
                          {sc.program_submission_required ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <span className="text-slate-500">Frequency:</span>{' '}
                          {sc.program_submission_frequency}
                        </p>
                        {sc.last_program_received && (
                          <p>
                            <span className="text-slate-500">Last Received:</span>{' '}
                            {new Date(sc.last_program_received).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap justify-end gap-2 mt-4 pt-4 border-t border-slate-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedForRequest(sc);
                        setRequestForm({
                          ...requestForm,
                          subject: `Program Submission Request - ${sc.company_name}`,
                          message: `Dear ${sc.primary_contact?.name},\n\nPlease submit your current construction program by the due date below.\n\nKind regards`
                        });
                        setRequestDialogOpen(true);
                      }}
                      data-testid={`request-program-${sc.id}`}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Request Program
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleEdit(sc); }}
                      data-testid={`edit-sc-${sc.id}`}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    {['admin', 'contracts_manager'].includes(normalizedRole) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDelete(sc.id, sc.company_name); }}
                        data-testid={`delete-sc-${sc.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Program Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">
              Request Program from {selectedForRequest?.company_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRequestProgram} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Request Type</Label>
              <Select value={requestForm.request_type} onValueChange={(v) => setRequestForm({ ...requestForm, request_type: v })}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial">Initial Program</SelectItem>
                  <SelectItem value="update">Progress Update</SelectItem>
                  <SelectItem value="recovery">Recovery Program</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Subject *</Label>
              <Input
                value={requestForm.subject}
                onChange={(e) => setRequestForm({ ...requestForm, subject: e.target.value })}
                required
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Message *</Label>
              <Textarea
                value={requestForm.message}
                onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
                required
                rows={4}
                className="rounded-sm resize-none"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Due Date *</Label>
                <Input
                  type="date"
                  value={requestForm.due_date}
                  onChange={(e) => setRequestForm({ ...requestForm, due_date: e.target.value })}
                  required
                  className="rounded-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Reminder Days Before</Label>
                <Input
                  type="number"
                  value={requestForm.reminder_days_before}
                  onChange={(e) => setRequestForm({ ...requestForm, reminder_days_before: parseInt(e.target.value) })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setRequestDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
                <Send className="h-4 w-4 mr-2" />
                Send Request
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
